import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgxMatInputTelComponent } from 'ngx-mat-input-tel';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { ImageInputComponent } from '../../../inputs/image-input/image-input.component';
import { CustomizableButtonComponent } from '../../../ui/customizable-button/customizable-button.component';
import { SettingsService } from '../../../../services/settings/settings.service';
import { UserProfileService } from '../../../../services/user/user-profile.service';
import { ToastService } from '../../../../services/toast/toast.service';
import { formatRoleName } from '../../../../pages/organization/settings/settings-roles';

type FeedbackType = 'success' | 'error' | 'info' | 'warning';

const NO_ACCOUNT = 'We could not read your account. Sign out, sign in again and retry.';

/** Your name, photo and phone number. The email is your sign-in and stays read-only. */
@Component({
  selector: 'app-profile-form',
  standalone: true,
  imports: [
    CommonModule,
    InputFieldComponent,
    ReactiveFormsModule,
    ImageInputComponent,
    CustomizableButtonComponent,
    NgxMatInputTelComponent,
  ],
  templateUrl: './profile-form.component.html',
  styleUrls: ['../../../../pages/organization/settings/settings-ui.css', './profile-form.component.css'],
})
export class ProfileFormComponent implements OnInit, OnDestroy {
  private builder = inject(FormBuilder);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private toast = inject(ToastService);

  settingsService = inject(SettingsService);
  userProfileService = inject(UserProfileService);

  editUserProfile: boolean = false;
  isUploadingImage: boolean = false;
  isUpdatingProfile: boolean = false;
  isDeletingImage: boolean = false;
  profileImageUrl: string | null = null;

  submitted: boolean = false;

  // Feedback: confirmations go to a toast; a failed save stays in the panel.
  feedbackMessage: string = '';
  feedbackType: FeedbackType = 'success';
  showFeedback: boolean = false;
  /** Why the last save failed, shown above the save row until the next try. */
  saveError = '';

  userProfileForm = this.builder.group({
    firstName: ['', Validators.required],
    lastName: ['', Validators.required],
    email: ['', [Validators.email, Validators.required]],
    primaryContactNumber: ['', Validators.required],
    profileImage: [''],
  });
  permissions: any[] = [];
  userRoles: string[] = [];

  /** The last profile the server sent, so Discard can put it back. */
  private lastProfile: any = null;
  /** The photo is fetched once per visit, and again after an upload. */
  private imageRequested = false;

  get userProfileData() {
    return this.userProfileService.userDetails;
  }

  /** Read-only: the sign-in email. */
  get email(): string {
    return this.userProfileForm.getRawValue().email || this.userProfileData?.email || '';
  }

  get emailErrorMsg() {
    const email = this.userProfileForm.get('email');
    if (email?.errors?.['email']) {
      return 'Enter a full email address, like name@company.com.';
    } else if (email?.errors?.['required']) {
      return 'Enter your email address.';
    } else {
      return '';
    }
  }

  get hasChanges(): boolean {
    return this.userProfileForm.dirty;
  }

  ngOnInit() {
    this.userProfileForm.get('email')?.disable();

    // Surface profile fetch failures
    this.settingsService.onErrorMessage
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ message, source }) => {
        if (source === 'fetchUserProfile') {
          this.displayFeedback(`Your profile did not load. ${message}`, 'error');
        }
      });

    this.settingsService.getUserProfileImageData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((imageData) => {
        if (imageData) {
          this.profileImageUrl = imageData;
          this.userProfileForm.patchValue({ profileImage: imageData }, { emitEvent: false });
        } else if (imageData === null) {
          // Removed, or there was never a photo
          this.profileImageUrl = null;
          this.userProfileForm.patchValue({ profileImage: '' }, { emitEvent: false });
        }
        this.cdr.markForCheck();
      });

    this.settingsService.fetchUserProfile();

    this.settingsService.getUserProfileData
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: any) => {
        if (!data) {
          return;
        }

        this.permissions = data?.access ? Object.keys(data.access) : [];
        this.userRoles =
          data?.realmRoles || data?.authorities || data?.roles || data?.accessRoles || [];

        this.lastProfile = data;
        // Unsaved edits are not overwritten by a background refresh.
        if (!this.userProfileForm.dirty || this.isUpdatingProfile) {
          this.applyProfile(data);
        }

        if (!this.imageRequested) {
          this.imageRequested = true;
          this.fetchUserProfileImage();
        }

        this.cdr.markForCheck();
      });
  }

  private applyProfile(data: any) {
    this.userProfileForm.patchValue({
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      primaryContactNumber: data?.attributes?.phoneNumber?.[0] || '',
      profileImage: data.profileImage,
    });
    if (data.profileImage) {
      this.profileImageUrl = data.profileImage;
    }
    this.userProfileForm.markAsPristine();
  }

  /** Puts back what is saved and drops the unsaved edits. */
  discardChanges() {
    this.submitted = false;
    this.saveError = '';
    if (this.lastProfile) {
      this.applyProfile(this.lastProfile);
    } else {
      this.userProfileForm.markAsPristine();
    }
  }

  fetchUserProfileImage() {
    const userId = this.userProfileData?.sub;
    if (!userId) {
      return;
    }

    this.settingsService.getUserProfileImage(userId).subscribe({
      error: () => {
        // A missing photo is not an error; anything else leaves the initials.
      },
    });
  }

  addProfileImage(imageFile: File | null) {
    if (!imageFile) {
      return;
    }

    const userId = this.userProfileData?.sub;
    if (!userId) {
      this.displayFeedback(NO_ACCOUNT, 'error');
      return;
    }

    this.isUploadingImage = true;

    this.settingsService.addUserProfileImage(userId, imageFile).subscribe({
      next: () => {
        // Refresh the user profile to get the updated image
        this.settingsService.fetchUserProfile();
        // Refetch the profile image to update the UI
        this.fetchUserProfileImage();
        this.isUploadingImage = false;
        this.displayFeedback('Photo updated.', 'success');
      },
      error: (error) => {
        this.isUploadingImage = false;
        const errorMessage =
          error?.status === 500
            ? 'The photo did not upload because of a problem on our side. Try again in a moment.'
            : error?.error?.detail ||
              error?.error?.message ||
              error?.message ||
              'The photo did not upload. Try a smaller PNG or JPG.';
        this.displayFeedback(errorMessage, 'error');
      },
    });
  }

  deleteProfileImage() {
    const userId = this.userProfileData?.sub;
    if (!userId) {
      this.displayFeedback(NO_ACCOUNT, 'error');
      return;
    }

    this.isDeletingImage = true;

    this.settingsService.removeUserProfileImage(userId).subscribe({
      next: () => {
        this.isDeletingImage = false;
        // The image subscription clears the preview
        this.displayFeedback('Photo removed. Your initials show instead.', 'success');
      },
      error: (error) => {
        this.isDeletingImage = false;
        const errorMessage =
          error?.status === 500
            ? 'The photo was not removed because of a problem on our side. Try again in a moment.'
            : error?.error?.detail ||
              error?.error?.message ||
              error?.message ||
              'The photo was not removed. Try again.';
        this.displayFeedback(errorMessage, 'error');
      },
    });
  }

  handleSubmit() {
    this.submitted = true;
    this.saveError = '';

    if (this.userProfileForm.invalid) {
      this.saveError = 'Some details are missing. Fill in the fields marked below.';
      return;
    }

    const userId = this.userProfileData?.sub;
    if (!userId) {
      this.saveError = NO_ACCOUNT;
      return;
    }

    // `.value` as before: the disabled email is left out of the request.
    const value = this.userProfileForm.value;
    const updateData = {
      firstName: value.firstName!,
      lastName: value.lastName!,
      emailAddress: value.email!,
      phoneNumber: value.primaryContactNumber!,
    };

    this.isUpdatingProfile = true;

    this.settingsService.updateUserProfile(userId, updateData).subscribe({
      next: () => {
        this.isUpdatingProfile = false;
        this.submitted = false;
        this.userProfileForm.markAsPristine();
        this.displayFeedback('Your details are saved.', 'success');
      },
      error: (error) => {
        this.isUpdatingProfile = false;
        this.saveError =
          error?.error?.message ||
          error?.message ||
          'Your details were not saved. Check your connection and try again.';
      },
    });
  }

  displayFeedback(message: string, type: FeedbackType) {
    this.feedbackMessage = message;
    this.feedbackType = type;
    this.showFeedback = true;
    if (type === 'error' || type === 'warning') {
      this.toast.error(message);
    } else {
      this.toast.success(message);
    }
  }

  formatRole(role: string): string {
    return formatRoleName(role);
  }

  ngOnDestroy() {
    // Subscriptions end with the component (takeUntilDestroyed).
  }
}
